import { FC, useMemo } from "react";

import { SessionStatsCard } from "./SessionStatsCard";

import { EngagementLevel, UserEngagementDataType } from "api/api-data-types";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { CircleInfoIcon, ThreeBarsIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectCurrentSectionId, userSelectors } from "store/selectors";
import { getUserFullName } from "utils/user-utils";

type EngagementCardProps = {
  engagementData: Record<string, UserEngagementDataType> | null;
};

export const EngagementCard: FC<EngagementCardProps> = ({ engagementData }) => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const { canAuthorSection } = useCapabilities();
  const users = useAppSelector(userSelectors.selectEntities);

  const {
    lowRiskPercentage,
    studentNeedingAttentionCount = 0,
    studentsNeedingAttentionUserIds = [],
  } = useMemo(() => {
    if (!engagementData) {
      return {
        lowRiskPercentage: 0,
        studentNeedingAttentionCount: 0,
        studentsNeedingAttentionUserIds: [],
      };
    }

    const userData = Object.entries(engagementData);

    if (userData.length === 0) {
      return {
        lowRiskPercentage: 0,
        studentNeedingAttentionCount: 0,
        studentsNeedingAttentionUserIds: [],
      };
    }

    // Count students at low risk (low risk = good engagement)
    const lowRiskCount = userData.filter(
      ([, data]) => data.risk_level === EngagementLevel.LOW,
    ).length;

    // Count students needing attention (medium or high risk)
    const needingAttention = userData.filter(
      ([, data]) =>
        data.risk_level === EngagementLevel.MEDIUM || data.risk_level === EngagementLevel.HIGH,
    );
    const needingAttentionCount = needingAttention.length;
    const needingAttentionUserIds = needingAttention.map(([userId]) => userId);

    const percentageLowRisk = Math.round((lowRiskCount / userData.length) * 100);

    return {
      lowRiskPercentage: percentageLowRisk,
      studentNeedingAttentionCount: needingAttentionCount,
      studentsNeedingAttentionUserIds: needingAttentionUserIds,
    };
  }, [engagementData]);

  // Format names for tooltip: "Name 1, Name 2 and Name 3 show signs of disengagement."
  const tooltipText = useMemo(() => {
    const studentNames = studentsNeedingAttentionUserIds.map((userId) => {
      const user = users[userId] ?? null;
      return getUserFullName(user);
    });
    studentNames.sort();
    if (studentNames.length === 0) return null;

    if (studentNames.length === 1) {
      return t("session_log_view.stats.engagement.tooltip_single", { name: studentNames[0] });
    }

    if (studentNames.length === 2) {
      return t("session_log_view.stats.engagement.tooltip_double", {
        name1: studentNames[0],
        name2: studentNames[1],
      });
    }

    // For 3+ names: "Name 1, Name 2 and Name 3 show signs..."
    const lastName = studentNames[studentNames.length - 1];
    const otherNames = studentNames.slice(0, -1).join(", ");
    return t("session_log_view.stats.engagement.tooltip_multiple", {
      names: otherNames,
      lastName,
    });
  }, [studentsNeedingAttentionUserIds, users]);

  if (!canAuthorSection || !sectionId) return null;

  const isEmpty = !engagementData || Object.keys(engagementData).length === 0;

  const footerTextContent = isEmpty
    ? t("session_log_view.stats.engagement.no_sessions")
    : studentNeedingAttentionCount > 0
      ? t("session_log_view.stats.engagement.footer", { count: studentNeedingAttentionCount })
      : t("session_log_view.stats.engagement.footer_none");

  const footerText =
    !isEmpty && tooltipText && studentNeedingAttentionCount > 0 ? (
      <Tooltip text={tooltipText} wrapperClassName="inline">
        <span>{footerTextContent}</span>
      </Tooltip>
    ) : (
      footerTextContent
    );

  return (
    <SessionStatsCard
      FooterIcon={CircleInfoIcon}
      footerText={footerText}
      isEmpty={isEmpty}
      kind="engagement"
      title={t("session_log_view.stats.engagement.title")}
      TitleIcon={ThreeBarsIcon}
      value={isEmpty ? 0 : `${lowRiskPercentage}%`}
      valueDescription={t("session_log_view.stats.engagement.value_description")}
    />
  );
};
